/**
 * Page « Guide-moi » (/aide) — guide client en 7 langues (fr, en, es, pt-BR, ar, ar-SY, zh).
 *
 * Publique (accessible depuis la vitrine et l'espace client). Rend le contenu vérifié de
 * `aideContent.js` (AIDE_SECTIONS / AIDE_LIMITS / AIDE_UI), une recherche, la visite interactive
 * (« Lecture guidée ») et la vidéo localisée (sous-titres incrustés + chapitres) — responsive
 * ordinateur + mobile, accessibilité clavier, RTL pour l'arabe.
 */
import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AIDE_SECTIONS, AIDE_LIMITS, AIDE_UI } from './aideContent';
import GuideTour from './GuideTour';

const resolveIcon = (name) => (name && Icons[name]) || Icons.HelpCircle;
const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Langues du guide (doivent correspondre EXACTEMENT aux codes `lang` de l'app).
const GLANGS = ['fr', 'en', 'es', 'pt-BR', 'ar', 'ar-SY', 'zh'];
// Boutons de bascule (ordre + libellés figés, demandés par Carlos).
const LANG_OPTIONS = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt-BR', label: 'Português (BR)' },
  { code: 'ar', label: 'العربية' },
  { code: 'ar-SY', label: 'Arabe syrien' },
  { code: 'zh', label: '简体中文' },
];
// Résout la langue de l'app vers une langue de guide supportée (zh-CN → zh, sinon repli fr).
const resolveGlang = (l) => {
  let raw = String(l || 'fr');
  if (raw === 'zh-CN') raw = 'zh';
  return GLANGS.includes(raw) ? raw : 'fr';
};
const isRtlGlang = (g) => g === 'ar' || g === 'ar-SY';

// Vidéos servies depuis /public/aide-media (chemin DISTINCT de la route /aide, pour éviter toute
// collision route SPA ↔ dossier statique). Sous-titres INCRUSTÉS dans le MP4 ; chapitres en JSON.
// fr/en existent ; les 5 autres pointent vers leur chemin FUTUR (repli EN/FR tant qu'absentes).
const VIDEOS = {
  fr: { src: '/aide-media/guide-fr-realtime.mp4', poster: '/aide-media/guide-fr-realtime.jpg', chapters: '/aide-media/guide-fr-realtime.chapters.json', label: 'Français' },
  en: { src: '/aide-media/guide-en-realtime.mp4', poster: '/aide-media/guide-en-realtime.jpg', chapters: '/aide-media/guide-en-realtime.chapters.json', label: 'English' },
  es: { src: '/aide-media/guide-es-realtime.mp4', poster: '/aide-media/guide-es-realtime.jpg', chapters: '/aide-media/guide-es-realtime.chapters.json', label: 'Español' },
  'pt-BR': { src: '/aide-media/guide-pt-realtime.mp4', poster: '/aide-media/guide-pt-realtime.jpg', chapters: '/aide-media/guide-pt-realtime.chapters.json', label: 'Português (BR)' },
  ar: { src: '/aide-media/guide-ar-realtime.mp4', poster: '/aide-media/guide-ar-realtime.jpg', chapters: '/aide-media/guide-ar-realtime.chapters.json', label: 'العربية' },
  'ar-SY': { src: '/aide-media/guide-ar-sy-realtime.mp4', poster: '/aide-media/guide-ar-sy-realtime.jpg', chapters: '/aide-media/guide-ar-sy-realtime.chapters.json', label: 'Arabe syrien' },
  zh: { src: '/aide-media/guide-zh-realtime.mp4', poster: '/aide-media/guide-zh-realtime.jpg', chapters: '/aide-media/guide-zh-realtime.chapters.json', label: '简体中文' },
};
const FALLBACK_LANG = VIDEOS.en ? 'en' : 'fr';

export default function AidePage() {
  const navigate = useNavigate();
  const { lang, isRtl } = useLanguage();
  // Langue du guide dérivée de la langue de l'app (7 langues), avec bascule manuelle.
  const [glang, setGlang] = useState(() => resolveGlang(lang));
  const [query, setQuery] = useState('');
  const [tourOpen, setTourOpen] = useState(false);
  // Vidéo : localisée d'abord, repli EN/FR si absente/erreur, placeholder si le repli échoue aussi.
  const [videoErrored, setVideoErrored] = useState(false);     // la vidéo localisée a échoué → repli
  const [fallbackErrored, setFallbackErrored] = useState(false); // le repli a aussi échoué → placeholder
  const [chapters, setChapters] = useState([]);
  const videoRef = useRef(null);
  const triedFallbackRef = useRef(false); // a-t-on déjà basculé sur le repli EN/FR ?

  useEffect(() => { setGlang(resolveGlang(lang)); }, [lang]);

  // Vidéo effectivement affichée : localisée, ou repli EN/FR.
  const usingFallback = videoErrored || !VIDEOS[glang];
  const shownLang = usingFallback ? FALLBACK_LANG : glang;
  const video = VIDEOS[shownLang] || VIDEOS.fr;
  const isFallbackShown = shownLang !== glang; // vrai quand on montre une autre langue que demandée

  // Réinitialise l'état vidéo à chaque changement de langue du guide.
  useEffect(() => { triedFallbackRef.current = false; setVideoErrored(false); setFallbackErrored(false); }, [glang]);

  // Charge les chapitres de la vidéo AFFICHÉE (liste cliquable). Sans réseau, la liste reste vide.
  useEffect(() => {
    let alive = true;
    setChapters([]);
    fetch(video.chapters).then((r) => (r.ok ? r.json() : [])).then((c) => { if (alive) setChapters(Array.isArray(c) ? c : []); }).catch(() => {});
    return () => { alive = false; };
  }, [video.chapters]);

  const seekTo = useCallback((sec) => {
    const v = videoRef.current;
    if (v) { try { v.currentTime = sec; v.play?.(); } catch (e) { /* ignore */ } }
  }, []);

  const onVideoError = useCallback(() => {
    // D'abord on tente le repli EN/FR ; s'il échoue à son tour, on affiche le placeholder.
    if (!triedFallbackRef.current && glang !== FALLBACK_LANG) {
      triedFallbackRef.current = true;
      setVideoErrored(true); // passe au repli EN/FR
    } else {
      setFallbackErrored(true); // repli indisponible aussi → placeholder
    }
  }, [glang]);

  const L = (obj) => (obj ? (obj[glang] ?? obj.fr ?? '') : '');
  const ui = (k) => L(AIDE_UI[k]);

  const sections = useMemo(() => {
    const q = norm(query).trim();
    if (!q) return AIDE_SECTIONS;
    const match = (item) => {
      const hay = [item.title, item.where, item.steps, item.can, item.cannot]
        .map((f) => (f ? [].concat(f[glang] || []).join(' ') + ' ' + (typeof f[glang] === 'string' ? '' : '') + (f[glang] || '') : ''))
        .join(' ');
      return norm(hay).includes(q);
    };
    return AIDE_SECTIONS
      .map((s) => ({ ...s, items: (s.items || []).filter(match) }))
      .filter((s) => s.items.length > 0 || norm(L(s.title) + ' ' + L(s.intro)).includes(q));
  }, [query, glang]);

  const rtl = isRtlGlang(glang) || (resolveGlang(lang) === glang && isRtl);
  const dir = rtl ? 'rtl' : 'ltr';

  const onKeyToggle = useCallback((e, code) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setGlang(code); }
  }, []);

  return (
    <div dir={dir} style={{ background: '#0b0f17', minHeight: '100vh', color: '#e5e7eb' }}>
      {tourOpen && <GuideTour glang={glang} onClose={() => setTourOpen(false)} />}

      {/* En-tête */}
      <header style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(11,15,23,0.92)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/')} aria-label={ui('homeAria')}
            title={ui('homeAria')}
            style={btn('ghost')} data-testid="aide-home">
            {React.createElement(Icons.Home, { size: 18 })}
            <span style={{ marginLeft: 6 }}>{ui('home')}</span>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 'auto' }}>
            {React.createElement(Icons.LifeBuoy, { size: 22, color: '#f87171' })}
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>{ui('title')}</div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>{ui('subtitle')}</div>
            </div>
          </div>
          {/* Bascule de langue (7 langues) */}
          <div role="group" aria-label={ui('langGroupAria')} style={{ display: 'flex', flexWrap: 'wrap', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10, overflow: 'hidden' }}>
            {LANG_OPTIONS.map(({ code, label }) => (
              <button key={code} onClick={() => setGlang(code)} onKeyDown={(e) => onKeyToggle(e, code)}
                aria-pressed={glang === code} data-testid={`aide-lang-${code}`} lang={code}
                style={{ padding: '7px 12px', border: 'none', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
                  background: glang === code ? '#ef4444' : 'transparent', color: glang === code ? '#fff' : '#cbd5e1' }}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={() => setTourOpen(true)} style={btn('primary')} data-testid="aide-start-tour">
            {React.createElement(Icons.PlayCircle, { size: 18 })}
            <span style={{ marginLeft: 6 }}>{ui('cinema')}</span>
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 1040, margin: '0 auto', padding: '20px 16px 64px' }}>
        <p style={{ fontSize: 15, color: '#cbd5e1', maxWidth: 720 }}>{ui('intro')}</p>

        {/* Recherche */}
        <div style={{ position: 'relative', margin: '18px 0 8px', maxWidth: 460 }}>
          {React.createElement(Icons.Search, { size: 16, color: '#9ca3af', style: { position: 'absolute', [rtl ? 'right' : 'left']: 12, top: 12 } })}
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={ui('search')}
            aria-label={ui('search')} data-testid="aide-search"
            style={{ width: '100%', padding: rtl ? '10px 34px 10px 12px' : '10px 12px 10px 34px', borderRadius: 10, background: '#111827', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.12)' }} />
        </div>

        {/* Vidéos */}
        <section aria-label={ui('videosAria')} style={{ margin: '18px 0 26px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            {React.createElement(Icons.Film, { size: 18, color: '#f87171' })}
            <h2 style={h2()}>{ui('videoTitle')}</h2>
            <span style={badge()}>{video.label}</span>
          </div>
          <div style={{ position: 'relative', width: '100%', maxWidth: 900, aspectRatio: '16 / 9', background: '#000', borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
            {!fallbackErrored ? (
              <video ref={videoRef} key={video.src} controls preload="metadata" playsInline poster={video.poster}
                data-testid={`aide-video-${glang}`} style={{ width: '100%', height: '100%', display: 'block' }}
                onError={onVideoError}>
                <source src={video.src} type="video/mp4" />
              </video>
            ) : (
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center', padding: 20, color: '#9ca3af' }}>
                {ui('videoFinalising')}
              </div>
            )}
          </div>
          {/* Note de repli : la vidéo localisée n'existe pas encore → version EN/FR affichée. */}
          {isFallbackShown && !fallbackErrored && (
            <p data-testid="aide-video-fallback-note" style={{ fontSize: 12.5, color: '#fcd34d', marginTop: 8 }}>
              {ui('videoFallbackNote')}
            </p>
          )}
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
            {ui('videoCaption')}
          </p>
          {chapters.length > 0 && (
            <div data-testid="aide-chapters" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {chapters.map((c, k) => (
                <button key={c.id || k} onClick={() => seekTo(c.start)} data-testid={`aide-chapter-${k}`}
                  style={{ ...badge(), cursor: 'pointer', color: '#e5e7eb' }}>
                  {String(k + 1).padStart(2, '0')} · {c.title}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Sections */}
        {sections.map((s) => {
          const SIcon = resolveIcon(s.icon);
          return (
            <section key={s.id} data-testid={`aide-section-${s.id}`} style={{ marginBottom: 30 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                {React.createElement(SIcon, { size: 20, color: '#f87171' })}
                <h2 style={h2()}>{L(s.title)}</h2>
              </div>
              {L(s.intro) && <p style={{ color: '#9ca3af', fontSize: 14, margin: '0 0 12px' }}>{L(s.intro)}</p>}
              <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                {(s.items || []).map((it) => {
                  const IIcon = resolveIcon(it.icon);
                  const steps = [].concat(L(it.steps) || []);
                  const can = [].concat(L(it.can) || []);
                  const cannot = [].concat(L(it.cannot) || []);
                  return (
                    <article key={it.id} data-testid={`aide-item-${it.id}`} style={card()}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        {React.createElement(IIcon, { size: 16, color: '#93c5fd' })}
                        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{L(it.title)}</h3>
                      </div>
                      {L(it.where) && (
                        <p style={{ fontSize: 12.5, color: '#93c5fd', margin: '0 0 8px' }}>
                          <strong>{ui('whereLabel')} : </strong>{L(it.where)}
                        </p>
                      )}
                      {steps.length > 0 && (
                        <ol style={{ margin: '0 0 8px', paddingInlineStart: 18, fontSize: 13.5, color: '#e5e7eb', lineHeight: 1.5 }}>
                          {steps.map((st, i) => <li key={i} style={{ marginBottom: 3 }}>{st}</li>)}
                        </ol>
                      )}
                      {can.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <div style={pill('#065f46', '#6ee7b7')}>{ui('canLabel')}</div>
                          <ul style={ul()}>{can.map((c, i) => <li key={i}>{c}</li>)}</ul>
                        </div>
                      )}
                      {cannot.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <div style={pill('#78350f', '#fcd34d')}>{ui('cannotLabel')}</div>
                          <ul style={ul()}>{cannot.map((c, i) => <li key={i}>{c}</li>)}</ul>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })}

        {/* Ce que vous ne pouvez pas faire */}
        <section data-testid="aide-limits" style={{ marginTop: 8, padding: 16, borderRadius: 14, background: 'rgba(120,53,15,0.18)', border: '1px solid rgba(252,211,77,0.25)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            {React.createElement(Icons.ShieldAlert, { size: 18, color: '#fcd34d' })}
            <h2 style={h2()}>{ui('limitsTitle')}</h2>
          </div>
          <ul style={{ ...ul(), color: '#fde68a' }}>{[].concat(AIDE_LIMITS[glang] || AIDE_LIMITS.fr || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
        </section>
      </main>
    </div>
  );
}

// styles utilitaires
const btn = (kind) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  padding: '8px 12px', borderRadius: 10, fontWeight: 600, fontSize: 14,
  border: kind === 'primary' ? 'none' : '1px solid rgba(255,255,255,0.14)',
  background: kind === 'primary' ? '#ef4444' : 'transparent',
  color: kind === 'primary' ? '#fff' : '#e5e7eb',
});
const h2 = () => ({ fontSize: 17, fontWeight: 700, margin: 0 });
const card = () => ({ background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 14 });
const pill = (bg, fg) => ({ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: bg, color: fg, marginBottom: 4 });
const ul = () => ({ margin: 0, paddingInlineStart: 18, fontSize: 13, lineHeight: 1.5 });
const badge = () => ({ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#1f2937', color: '#93c5fd', border: '1px solid rgba(255,255,255,0.1)' });
